import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { canvasPixelsToMM, mmToCanvasPixels } from '../../Editor/DecalEditorCanvasSizing';
import { OBJECT_CHANGED } from '../../Rutab/RutabConstants';
import { saveCanvasState } from '../../Actions/Rutab/HistoryActions';
import { debounce } from 'lodash-es';
import { translate } from '../../Services/translation';
import { useActiveObjectLocationChanged } from '../../Editor/Hooks/useReadOnlyState';
const PositionFloatingMenu = memo(({ canvas, activeObject, showPositionMenu, initialPosition }) => {
    const [objectPosition, setObjectPosition] = useState({ top: 0, left: 0 });
    const [objectInfo, setObjectInfo] = useState({
        width: 0,
        height: 0,
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
    });
    const [menuPosition, setMenuPosition] = useState(initialPosition);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const dispatch = useDispatch();
    const activeObjectLocationChanged = useActiveObjectLocationChanged();
    useEffect(() => {
        if (!isDragging) {
            setMenuPosition(initialPosition);
        }
    }, [initialPosition]);
    const debouncedObjectChanged = useCallback(debounce((obj) => {
        dispatch({
            type: OBJECT_CHANGED,
            payload: { activeObjectLocationChanged: obj.getBoundingRect() },
        });
        dispatch(saveCanvasState(canvas));
    }, 200), [dispatch, canvas]);
    const updateObjectInfo = useCallback(() => {
        if (activeObject.length > 0 && activeObject[0]) {
            const obj = activeObject[0];
            const boundingRect = obj.getBoundingRect();
            const leftMM = Math.round(canvasPixelsToMM(obj.left || 0) * 100) / 100;
            const topMM = Math.round(canvasPixelsToMM(obj.top || 0) * 100) / 100;
            const widthMM = Math.round(canvasPixelsToMM(boundingRect.width) * 100) / 100;
            const heightMM = Math.round(canvasPixelsToMM(boundingRect.height) * 100) / 100;
            const rightMM = Math.round((leftMM + widthMM) * 100) / 100;
            const bottomMM = Math.round((topMM + heightMM) * 100) / 100;
            setObjectPosition({
                top: topMM,
                left: leftMM,
            });
            setObjectInfo({
                width: widthMM,
                height: heightMM,
                left: leftMM,
                right: rightMM,
                top: topMM,
                bottom: bottomMM
            });
        }
    }, [activeObject, canvas]);
    useEffect(() => {
        updateObjectInfo();
    }, [updateObjectInfo, activeObjectLocationChanged]);
    const handlePositionChange = useCallback((field, value) => {
        const mmValue = parseFloat(value) || 0;
        setObjectPosition((prev) => (Object.assign(Object.assign({}, prev), { [field]: mmValue })));
        if (activeObject.length > 0 && activeObject[0]) {
            const obj = activeObject[0];
            const canvasPixelValue = mmToCanvasPixels(mmValue);
            obj.set(field, canvasPixelValue);
            obj.setCoords();
            canvas.renderAll();
            debouncedObjectChanged(obj);
        }
    }, [activeObject, canvas, debouncedObjectChanged]);
    const handleMouseDown = useCallback((e) => {
        var _a;
        setIsDragging(true);
        const rect = (_a = e.target.closest('.settings-floating-menu')) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        if (rect) {
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
        }
    }, []);
    const handleMouseMove = useCallback((e) => {
        if (!isDragging)
            return;
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        const windowWidth = 320;
        const windowHeight = 300;
        const rightPosition = window.innerWidth - newX - windowWidth;
        const maxRight = window.innerWidth - windowWidth;
        const minRight = 0;
        setMenuPosition({
            right: Math.max(minRight, Math.min(rightPosition, maxRight)),
            top: Math.max(0, Math.min(newY, window.innerHeight - windowHeight))
        });
    }, [isDragging, dragOffset.x, dragOffset.y]);
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);
    if (!showPositionMenu || activeObject.length === 0) {
        return null;
    }
    return (_jsxs("div", { className: `settings-floating-menu ${isDragging ? 'dragging' : ''}`, style: {
            position: 'fixed',
            top: menuPosition.top,
            right: menuPosition.right,
            zIndex: isDragging ? 1001 : 1000
        }, children: [_jsxs("div", { className: "position-menu-header draggable-header", onMouseDown: handleMouseDown, style: { cursor: isDragging ? 'grabbing' : 'grab' }, children: [_jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", fill: "currentColor", className: "bi bi-crosshair", viewBox: "0 0 16 16", children: _jsx("path", { d: "M8.5.5a.5.5 0 0 0-1 0v.518A7 7 0 0 0 1.018 7.5H.5a.5.5 0 0 0 0 1h.518A7 7 0 0 0 7.5 14.982v.518a.5.5 0 0 0 1 0v-.518A7 7 0 0 0 14.982 8.5h.518a.5.5 0 0 0 0-1h-.518A7 7 0 0 0 8.5 1.018zm-6.48 7A6 6 0 0 1 7.5 2.02v.48a.5.5 0 0 0 1 0v-.48a6 6 0 0 1 5.48 5.48h-.48a.5.5 0 0 0 0 1h.48a6 6 0 0 1-5.48 5.48v-.48a.5.5 0 0 0-1 0v.48A6 6 0 0 1 2.02 8.5h.48a.5.5 0 0 0 0-1zM8 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4" }) }), _jsx("h4", { children: translate('decal.settings.object.position') }), _jsx("div", { className: "drag-indicator", children: _jsx("svg", { width: "12", height: "12", viewBox: "0 0 24 24", fill: "none", children: _jsx("path", { d: "M8 6H16M8 12H16M8 18H16", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }) }) })] }), _jsxs("div", { className: "position-menu-content", children: [_jsxs("div", { className: "position-field", children: [_jsxs("label", { children: [translate('decal.settings.object.left'), ":"] }), _jsx("input", { type: "number", value: objectPosition.left, onChange: (e) => handlePositionChange('left', e.target.value), className: "position-input", step: "1" }), _jsx("span", { children: "mm" })] }), _jsxs("div", { className: "position-field", children: [_jsxs("label", { children: [translate('decal.settings.object.top'), ":"] }), _jsx("input", { type: "number", value: objectPosition.top, onChange: (e) => handlePositionChange('top', e.target.value), className: "position-input", step: "1" }), _jsx("span", { children: "mm" })] }), _jsxs("div", { className: "object-info", children: [_jsxs("div", { className: "info-row", children: [_jsxs("div", { className: "info-item", children: [_jsxs("strong", { children: [translate('decal.settings.object.width'), ":"] }), " ", objectInfo.width, " mm"] }), _jsxs("div", { className: "info-item", children: [_jsxs("strong", { children: [translate('decal.settings.object.height'), ":"] }), " ", objectInfo.height, " mm"] })] }), _jsxs("div", { className: "info-row", children: [_jsxs("div", { className: "info-item", children: [_jsxs("strong", { children: [translate('decal.settings.object.left'), ":"] }), " ", objectInfo.left, " mm"] }), _jsxs("div", { className: "info-item", children: [_jsxs("strong", { children: [translate('decal.settings.object.right'), ":"] }), " ", objectInfo.right, " mm"] })] }), _jsxs("div", { className: "info-row", children: [_jsxs("div", { className: "info-item", children: [_jsxs("strong", { children: [translate('decal.settings.object.top'), ":"] }), " ", objectInfo.top, " mm"] }), _jsxs("div", { className: "info-item", children: [_jsxs("strong", { children: [translate('decal.settings.object.bottom'), ":"] }), " ", objectInfo.bottom, " mm"] })] })] })] })] }));
});
export default PositionFloatingMenu;
